// File: src/components/layouts/bars/SidebarEntry/SidebarEntry.tsx
// Purpose: Layout or container component

import React from "react";
import styled from "styled-components";
import { MessageSquare } from "lucide-react";
import { Typography } from "../../../general/Typography";
import { NotificationIcon } from "../../../general/NotificationIcon";
import { Tooltip } from "../../../general/Tooltip";

interface SidebarEntryProps {
  icon?: React.ReactNode;
  label: string;
  path?: string;
  count?: number;
  active?: boolean;
  compact?: boolean;
  onClick?: () => void;
}

const EntryWrapper = styled.div<{ active?: boolean; compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ theme, compact }) => (compact ? "0" : theme.spacing.sm)};
  padding: ${({ theme }) => theme.spacing.md};
  cursor: pointer;
  transition: background 0.2s ease-in-out;
  border-radius: ${({ theme }) => theme.radii.md};
  color: ${({ theme, active }) =>
    active ? theme.colors.primary : theme.colors.textPrimary};
  background: ${({ theme, active }) =>
    active ? theme.colors.surface : "transparent"};
  position: relative;
  width: ${({ compact }) => (compact ? "48px" : "100%")}; 
  justify-content: ${({ compact }) => (compact ? "center" : "flex-start")};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }

  /* ✅ Mobile Adjustments */
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: ${({ theme }) => theme.spacing.sm};
    width: ${({ compact }) => (compact ? "44px" : "100%")}; 
  }
`;

const IconContainer = styled.div<{ compact?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  position: relative; /* ✅ Ensure badge aligns properly */

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    width: 36px;
    height: 36px;
  }
`;

/* ✅ Fix for Tooltip visibility */
const TooltipWrapper = styled.div`
  position: relative;
  z-index: 1300; /* Higher than sidebar */
  pointer-events: none;
  white-space: nowrap; /* Prevents tooltip from wrapping */
`;

export const SidebarEntry: React.FC<SidebarEntryProps> = ({
  icon = <MessageSquare size={22} />,
  label,
  count = 0,
  active = false,
  compact = false,
  path,
  onClick,
}) => {
  const validIcon = React.isValidElement(icon) ? icon : <MessageSquare size={22} />;

  return compact ? (
    
        <EntryWrapper onClick={onClick} active={active} compact={compact}>
          <IconContainer compact={compact}>
            <NotificationIcon icon={validIcon} count={count} />
          </IconContainer>
        </EntryWrapper>
    // <TooltipWrapper>
    //   <Tooltip content={label} position="right">  </Tooltip>
    // </TooltipWrapper>
  ) : (
    <EntryWrapper onClick={onClick} active={active} compact={compact}>
      <IconContainer compact={compact}>
        <NotificationIcon icon={validIcon} count={count} />
      </IconContainer>
      {!compact && <Typography as="span" variant="body">{label}</Typography>}
    </EntryWrapper>
  );
};